import React, { useState, useEffect } from 'react';
import { api, apiFetch } from '../lib/api';
import { Card, Btn } from '../components/ui';
import { TimePicker } from './SheetBuilder';
import { useConfirm } from '../components/ConfirmModal';
import useIsMobile from '../hooks/useIsMobile';
import EmailSearch from '../components/EmailSearch';

function parseContacts(text) {
  const contacts = [];
  const lines = text.split(/[,\n;]+/).map(l => l.trim()).filter(Boolean);
  for (const line of lines) {
    // "Name" <email> or Name <email>
    const match = line.match(/^"?([^"<]*?)"?\s*<([^>]+)>/);
    if (match) {
      contacts.push({ name: match[1].trim(), email: match[2].trim().toLowerCase() });
    } else {
      // Plain email
      const emailMatch = line.match(/[\w.+-]+@[\w.-]+\.\w+/);
      if (emailMatch) {
        const email = emailMatch[0].toLowerCase();
        const name = line.replace(emailMatch[0], '').replace(/[<>"]/g, '').trim();
        contacts.push({ name: name || email.split('@')[0], email });
      }
    }
  }
  // Dedupe by email
  const seen = new Set();
  return contacts.filter(c => { if (seen.has(c.email)) return false; seen.add(c.email); return true; });
}

export default function DistributionPage() {
  const isMobile = useIsMobile();
  const [confirmAction, ConfirmDialog] = useConfirm();
  const [artists, setArtists] = useState([]);
  const [selArtist, setSelArtist] = useState('');
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [showBulk, setShowBulk] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [editGroup, setEditGroup] = useState(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newGroup, setNewGroup] = useState('');
  const [filterGroup, setFilterGroup] = useState('all');
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [sheets, setSheets] = useState([]);

  useEffect(() => {
    api.getArtists().then(d => {
      const list = d.artists || [];
      setArtists(list);
      if (list.length) loadArtist(list[0]);
    });
  }, []);

  const loadArtist = async (a) => {
    setSelArtist(a.id);
    const currentContacts = a.distributionContacts || [];
    setContacts(currentContacts);
    setGroups(a.distributionGroups || []);
    setFilterGroup('all');
    api.getSheets(a.id).then(d => setSheets((d.sheets || d || []).sort((a,b) => a.date.localeCompare(b.date)))).catch(() => setSheets([]));

    // Auto-sync: add linked app users who aren't in distribution yet
    try {
      const users = await apiFetch('/users');
      const existingEmails = new Set(currentContacts.map(c => c.email.toLowerCase()));
      const missing = (users || []).filter(u =>
        u.email && !existingEmails.has(u.email.toLowerCase()) &&
        (u.linkedArtistIds || []).includes(a.id)
      );
      if (missing.length) {
        const newContacts = missing.map(u => ({
          id: 'c-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6),
          name: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email.split('@')[0],
          email: u.email.toLowerCase(),
          groupIds: [],
        }));
        const merged = [...currentContacts, ...newContacts];
        setContacts(merged);
        // Silent save
        await apiFetch(`/artists/${a.id}`, {
          method: 'PATCH',
          body: JSON.stringify({
            distributionContacts: merged,
            distributionList: merged.map(c => c.email),
          }),
        });
        setArtists(prev => prev.map(ar => ar.id === a.id ? { ...ar, distributionContacts: merged, distributionList: merged.map(c => c.email) } : ar));
      }
    } catch {}
  };

  const save = async (newContacts, newGroups) => {
    setSaving(true);
    try {
      // Also sync distributionList for backward compat
      const emailList = (newContacts || contacts).map(c => c.email);
      await apiFetch(`/artists/${selArtist}`, {
        method: 'PATCH',
        body: JSON.stringify({
          distributionContacts: newContacts || contacts,
          distributionGroups: newGroups || groups,
          distributionList: emailList,
        }),
      });
      setArtists(prev => prev.map(a => a.id === selArtist ? {
        ...a,
        distributionContacts: newContacts || contacts,
        distributionGroups: newGroups || groups,
        distributionList: emailList,
      } : a));
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  // ── Group CRUD ──
  const addGroup = () => {
    if (!newGroupName.trim()) return;
    const g = { id: 'g-' + Date.now(), name: newGroupName.trim() };
    const updated = [...groups, g];
    setGroups(updated);
    setNewGroupName('');
    save(null, updated);
  };

  const deleteGroup = async (gid) => {
    if (!await confirmAction('Delete this group? Contacts will become ungrouped.', { title: 'Delete Group', confirmLabel: 'Delete' })) return;
    const updatedGroups = groups.filter(g => g.id !== gid);
    const updatedContacts = contacts.map(c => ({
      ...c,
      groupIds: (c.groupIds || []).filter(id => id !== gid),
    }));
    setGroups(updatedGroups);
    setContacts(updatedContacts);
    save(updatedContacts, updatedGroups);
  };

  const renameGroup = () => {
    if (!editGroup || !editGroupName.trim()) return;
    const updated = groups.map(g => g.id === editGroup ? { ...g, name: editGroupName.trim() } : g);
    setGroups(updated);
    setEditGroup(null);
    save(null, updated);
  };

  // ── Contact CRUD ──
  const addContact = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email.includes('@')) return;
    if (contacts.some(c => c.email === email)) { setNewEmail(''); return; }
    const c = { id: 'c-' + Date.now(), name: newName.trim() || email.split('@')[0], email, groupIds: newGroup ? [newGroup] : [] };
    const updated = [...contacts, c];
    setContacts(updated);
    setNewEmail('');
    setNewName('');
    setNewGroup('');
    save(updated);
  };

  const removeContact = (cid) => {
    const updated = contacts.filter(c => c.id !== cid);
    setContacts(updated);
    save(updated);
  };

  const toggleContactGroup = (contactId, groupId) => {
    const updated = contacts.map(c => {
      if (c.id !== contactId) return c;
      const gids = c.groupIds || [];
      return { ...c, groupIds: gids.includes(groupId) ? gids.filter(g => g !== groupId) : [...gids, groupId] };
    });
    setContacts(updated);
    save(updated);
  };

  // ── Bulk paste ──
  const handleBulkAdd = () => {
    const parsed = parseContacts(bulkText);
    if (!parsed.length) { alert('No valid emails found'); return; }
    const existingEmails = new Set(contacts.map(c => c.email));
    const newOnes = parsed.filter(c => !existingEmails.has(c.email)).map(c => ({
      ...c, id: 'c-' + Date.now() + '-' + Math.random().toString(36).slice(2,6), groupIds: [],
    }));
    if (!newOnes.length) { alert('All emails already exist'); setBulkText(''); setShowBulk(false); return; }
    const updated = [...contacts, ...newOnes];
    setContacts(updated);
    setBulkText('');
    setShowBulk(false);
    save(updated);
    alert(`Added ${newOnes.length} new contact${newOnes.length !== 1 ? 's' : ''}${parsed.length - newOnes.length > 0 ? ` (${parsed.length - newOnes.length} duplicates skipped)` : ''}`);
  };

  const handleTestEmail = async () => {
    const to = testEmail.trim() || contacts[0]?.email;
    if (!to) { alert('Enter an email'); return; }
    setTestSending(true);
    try {
      await apiFetch('/email/test', { method: 'POST', body: JSON.stringify({ to }) });
      alert('Test email sent to ' + to);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setTestSending(false); }
  };

  // ── Filter contacts ──
  const filtered = filterGroup === 'all' ? contacts
    : filterGroup === 'ungrouped' ? contacts.filter(c => !c.groupIds || c.groupIds.length === 0)
    : contacts.filter(c => (c.groupIds || []).includes(filterGroup));

  const selectedArtist = artists.find(a => a.id === selArtist);
  const inp = { background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 14px', fontSize:14, color:'var(--text)', outline:'none', fontFamily:'inherit', width:'100%', boxSizing:'border-box' };

  return (
    <div style={{ padding: window.innerWidth < 768 ? '20px 16px' : '32px 40px', maxWidth:1000, margin:'0 auto' }}>
      {/* Artist picker — circle avatars */}
      {artists.length > 1 && (
        <div style={{ display:'flex', gap:14, marginBottom:20, flexWrap:'wrap', justifyContent:'center' }}>
          {artists.map(a => {
            const isSel = selArtist===a.id;
            const contactCount = (a.distributionContacts||a.distributionList||[]).length;
            return (
              <button key={a.id} onClick={() => loadArtist(a)}
                style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, padding:0, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', opacity: isSel ? 1 : 0.6, transition:'all 0.15s' }}>
                <div style={{ width:48, height:48, borderRadius:'50%', border:`2.5px solid ${isSel ? 'var(--blue)' : 'var(--border)'}`,
                  display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, overflow:'hidden',
                  background: a.photo ? 'none' : `${a.color||'var(--card)'}15`,
                  boxShadow: isSel ? '0 0 0 3px rgba(79,156,249,0.25)' : 'none', transition:'all 0.15s', position:'relative' }}>
                  {a.photo ? <img src={a.photo} alt={a.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (a.emoji||a.name[0])}
                  {contactCount > 0 && (
                    <div style={{ position:'absolute', bottom:-2, right:-2, minWidth:16, height:16, borderRadius:8, background:'var(--blue)', display:'flex', alignItems:'center', justifyContent:'center', padding:'0 4px', border:'2px solid var(--card)' }}>
                      <span style={{ fontSize:9, fontWeight:800, color:'#fff' }}>{contactCount}</span>
                    </div>
                  )}
                </div>
                <span style={{ fontSize:10, fontWeight:700, color: isSel ? 'var(--blue)' : 'var(--text2)', maxWidth:56, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.name?.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Action button */}
      <button onClick={() => setShowBulk(true)}
        style={{ width:'100%', padding:'16px', borderRadius:14, border:'none', marginBottom:16,
          background:'linear-gradient(135deg, #1E40AF, #6366f1)',
          boxShadow:'0 4px 15px rgba(30,64,175,0.3)',
          cursor:'pointer', fontFamily:'inherit', fontSize:15, fontWeight:800, color:'#fff',
          transition:'all 0.2s', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}
        onMouseEnter={e => { e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 6px 20px rgba(30,64,175,0.4)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 4px 15px rgba(30,64,175,0.3)'; }}>
        <i className="fa-solid fa-paste" style={{ fontSize:13 }}></i>Bulk Add Contacts
      </button>

      {/* Email Send Time — single global setting per artist */}
      <Card style={{ padding:20, marginBottom:20 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:40, height:40, borderRadius:12, background:'rgba(79,156,249,0.1)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <i className="fa-solid fa-clock" style={{ fontSize:16, color:'var(--blue)' }}></i>
            </div>
            <div>
              <p style={{ fontSize:14, fontWeight:700, color:'var(--text)', margin:0 }}>Daily Send Time</p>
              <p style={{ fontSize:12, color:'var(--text3)', margin:'2px 0 0' }}>
                {selectedArtist?.emailSendTime
                  ? (() => { const [h,m] = selectedArtist.emailSendTime.split(':').map(Number); return `Emails send at ${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'} when published`; })()
                  : 'Set a time to auto-send published day sheets'}
              </p>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
            <div style={{ width:140 }}>
              <TimePicker
                value={selectedArtist?.emailSendTime || ''}
                onChange={async (time) => {
                  const val = time || null;
                  await apiFetch(`/artists/${selArtist}`, { method:'PATCH', body: JSON.stringify({ emailSendTime: val }) });
                  setArtists(prev => prev.map(a => a.id === selArtist ? { ...a, emailSendTime: val } : a));
                }}
                placeholder="Set time"
              />
            </div>
            {selectedArtist?.emailSendTime && (
              <button onClick={async () => {
                await apiFetch(`/artists/${selArtist}`, { method:'PATCH', body: JSON.stringify({ emailSendTime: null }) });
                setArtists(prev => prev.map(a => a.id === selArtist ? { ...a, emailSendTime: null } : a));
              }}
                style={{ background:'none', border:'1px solid var(--border)', borderRadius:8, padding:'8px 10px',
                  cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:600, color:'var(--text3)' }}>
                Clear
              </button>
            )}
          </div>
        </div>
      </Card>

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '280px 1fr', gap: isMobile ? 16 : 24, alignItems:'start' }}>
        {/* Left — Groups */}
        <div>
          <Card style={{ padding:20, marginBottom:16 }}>
            <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', margin:'0 0 14px', textTransform:'uppercase' }}>
              <i className="fa-solid fa-folder" style={{ marginRight:5, fontSize:9 }}></i> Groups
            </p>

            {/* Add group */}
            <div style={{ display:'flex', gap:6, marginBottom:14 }}>
              <input value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addGroup(); }}
                placeholder="New group name..."
                style={{ ...inp, fontSize:13, padding:'8px 12px' }} />
              <button onClick={addGroup}
                style={{ padding:'8px 12px', borderRadius:8, background:'var(--blue)', border:'none', color:'white', cursor:'pointer', fontSize:12, fontWeight:700, flexShrink:0 }}>
                <i className="fa-solid fa-plus"></i>
              </button>
            </div>

            {/* Group list */}
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <button onClick={() => setFilterGroup('all')}
                style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', borderRadius:8, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:600, textAlign:'left',
                  background: filterGroup==='all' ? 'rgba(30,64,175,0.1)' : 'transparent',
                  color: filterGroup==='all' ? 'var(--blue)' : 'var(--text2)' }}>
                <span><i className="fa-solid fa-users" style={{ marginRight:8, fontSize:11 }}></i>All Contacts</span>
                <span style={{ fontSize:11, color:'var(--text3)' }}>{contacts.length}</span>
              </button>

              {groups.map(g => {
                const count = contacts.filter(c => (c.groupIds||[]).includes(g.id)).length;
                return (
                  <div key={g.id} style={{ display:'flex', alignItems:'center', gap:4 }}>
                    <button onClick={() => setFilterGroup(g.id)}
                      style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', borderRadius:8, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:600, textAlign:'left',
                        background: filterGroup===g.id ? 'rgba(30,64,175,0.1)' : 'transparent',
                        color: filterGroup===g.id ? 'var(--blue)' : 'var(--text2)' }}>
                      {editGroup === g.id ? (
                        <input value={editGroupName} onChange={e => setEditGroupName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') renameGroup(); if (e.key === 'Escape') setEditGroup(null); }}
                          onBlur={renameGroup}
                          autoFocus onClick={e => e.stopPropagation()}
                          style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:6, padding:'2px 8px', fontSize:13, color:'var(--text)', outline:'none', fontFamily:'inherit', width:'100%' }} />
                      ) : (
                        <span><i className="fa-solid fa-tag" style={{ marginRight:8, fontSize:10 }}></i>{g.name}</span>
                      )}
                      <span style={{ fontSize:11, color:'var(--text3)' }}>{count}</span>
                    </button>
                    <button onClick={() => { setEditGroup(g.id); setEditGroupName(g.name); }}
                      style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:11, padding:'4px' }}>
                      <i className="fa-solid fa-pen"></i>
                    </button>
                    <button onClick={() => deleteGroup(g.id)}
                      style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:11, padding:'4px' }}>
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  </div>
                );
              })}

              <button onClick={() => setFilterGroup('ungrouped')}
                style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', borderRadius:8, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:600, textAlign:'left',
                  background: filterGroup==='ungrouped' ? 'rgba(30,64,175,0.1)' : 'transparent',
                  color: filterGroup==='ungrouped' ? 'var(--blue)' : 'var(--text3)' }}>
                <span><i className="fa-regular fa-folder-open" style={{ marginRight:8, fontSize:11 }}></i>Ungrouped</span>
                <span style={{ fontSize:11, color:'var(--text3)' }}>{contacts.filter(c => !c.groupIds || c.groupIds.length === 0).length}</span>
              </button>
            </div>
          </Card>

          {/* Test email */}
          <Card style={{ padding:20 }}>
            <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', margin:'0 0 10px', textTransform:'uppercase' }}>
              <i className="fa-solid fa-paper-plane" style={{ marginRight:5, fontSize:9 }}></i> Test Email
            </p>
            <div style={{ marginBottom:8 }}>
              <EmailSearch value={testEmail} onChange={setTestEmail} placeholder="Search contacts or type email..." />
            </div>
            <button onClick={handleTestEmail} disabled={testSending}
              style={{ width:'100%', padding:'10px', borderRadius:10, background:'var(--blue)', border:'none',
                color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor: testSending ? 'wait' : 'pointer',
                opacity: testSending ? 0.6 : 1 }}>
              <i className="fa-solid fa-paper-plane" style={{ marginRight:6, fontSize:10 }}></i>
              {testSending ? 'Sending...' : 'Send Test'}
            </button>
          </Card>
        </div>

        {/* Right — Contacts */}
        <div>
          <Card style={{ padding:20 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
              <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', margin:0, textTransform:'uppercase' }}>
                <i className="fa-solid fa-address-book" style={{ marginRight:5, fontSize:9 }}></i>
                {filterGroup === 'all' ? 'All Contacts' : filterGroup === 'ungrouped' ? 'Ungrouped' : groups.find(g => g.id === filterGroup)?.name || 'Contacts'}
              </p>
              <span style={{ fontSize:12, color:'var(--text3)' }}>
                {filtered.length} contact{filtered.length !== 1 ? 's' : ''}
                {saving && <span style={{ color:'var(--blue)', marginLeft:8 }}><i className="fa-solid fa-circle-notch fa-spin"></i></span>}
              </span>
            </div>

            {/* Add single contact */}
            <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              <input value={newName} onChange={e => setNewName(e.target.value)}
                placeholder="Name"
                style={{ ...inp, flex:1, fontSize:13, padding:'8px 12px' }} />
              <div style={{ flex:1.5 }}>
                <EmailSearch value={newEmail} onChange={setNewEmail} placeholder="Search or type email..." style={{ ...inp, fontSize:13, padding:'8px 12px' }} />
              </div>
              <select value={newGroup} onChange={e => setNewGroup(e.target.value)}
                style={{ ...inp, flex:0.8, fontSize:12, padding:'8px 10px', colorScheme:'dark' }}>
                <option value="">No group</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <button onClick={addContact}
                style={{ padding:'8px 14px', borderRadius:8, background:'var(--blue)', border:'none', color:'white', cursor:'pointer', fontSize:12, fontWeight:700, flexShrink:0 }}>
                <i className="fa-solid fa-plus"></i> Add
              </button>
            </div>

            {/* Contact list */}
            {filtered.length === 0 ? (
              <div style={{ textAlign:'center', padding:'40px 0' }}>
                <i className="fa-regular fa-address-book" style={{ fontSize:32, color:'var(--text3)', display:'block', marginBottom:12 }}></i>
                <p style={{ fontSize:14, color:'var(--text3)' }}>No contacts{filterGroup !== 'all' ? ' in this group' : ''}</p>
                <p style={{ fontSize:12, color:'var(--text3)', opacity:0.6, marginTop:4 }}>Add contacts above or use Bulk Paste</p>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                {filtered.map(c => (
                  <div key={c.id} style={{ padding:'12px 14px', background:'var(--bg)', borderRadius:10, border:'1px solid var(--border)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:34, height:34, borderRadius:'50%', background:'rgba(30,64,175,0.1)', border:'1px solid rgba(30,64,175,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <span style={{ fontSize:12, fontWeight:800, color:'var(--blue)' }}>
                          {(c.name || c.email).substring(0,2).toUpperCase()}
                        </span>
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <p style={{ fontSize:14, fontWeight:700, color:'var(--text)', margin:0 }}>{c.name || c.email}</p>
                        {c.name && <p style={{ fontSize:11, color:'var(--text3)', margin:'1px 0 0' }}>{c.email}</p>}
                      </div>
                      <button onClick={() => removeContact(c.id)}
                        style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:12, padding:'4px 6px', flexShrink:0 }}>
                        <i className="fa-solid fa-xmark"></i>
                      </button>
                    </div>
                    {/* Group tags — wrap below name */}
                    <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginTop:8 }}>
                      {groups.map(g => {
                        const inGroup = (c.groupIds || []).includes(g.id);
                        return (
                          <button key={g.id} onClick={() => toggleContactGroup(c.id, g.id)}
                            style={{ fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:10, cursor:'pointer', fontFamily:'inherit', border:'1px solid', transition:'all 0.15s',
                              background: inGroup ? 'rgba(30,64,175,0.12)' : 'transparent',
                              borderColor: inGroup ? 'rgba(30,64,175,0.3)' : 'var(--border)',
                              color: inGroup ? 'var(--blue)' : 'var(--text3)' }}>
                            {g.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {ConfirmDialog}

      {/* Bulk Paste Modal */}
      {showBulk && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
          onClick={e => e.target === e.currentTarget && setShowBulk(false)}>
          <div style={{ background:'var(--card)', borderRadius:16, padding:28, width:560, maxWidth:'90vw', border:'1px solid var(--border)', maxHeight:'80vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <h3 style={{ fontSize:18, fontWeight:800, color:'var(--text)', margin:0 }}>
                <i className="fa-solid fa-paste" style={{ marginRight:8, color:'var(--blue)' }}></i>
                Bulk Add Contacts
              </h3>
              <button onClick={() => setShowBulk(false)} style={{ background:'none', border:'none', fontSize:18, cursor:'pointer', color:'var(--text3)' }}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <p style={{ fontSize:13, color:'var(--text2)', marginBottom:16, lineHeight:1.6 }}>
              Paste a list of contacts. Supports formats like:<br/>
              <code style={{ fontSize:11, color:'var(--text3)' }}>Name &lt;email@example.com&gt;</code> or plain emails, comma/newline separated.
            </p>
            <textarea value={bulkText} onChange={e => setBulkText(e.target.value)}
              placeholder={'Danielle Jonas <DJ@jonasenterprises.com>,\nHolly Farr <holly@philymack.com>,\nspencer@crew.com'}
              rows={10}
              style={{ ...inp, resize:'vertical', fontFamily:'monospace', fontSize:12, lineHeight:1.6 }} />
            {bulkText && (
              <p style={{ fontSize:12, color:'var(--text3)', marginTop:8 }}>
                <i className="fa-solid fa-circle-info" style={{ marginRight:4 }}></i>
                {parseContacts(bulkText).length} contact{parseContacts(bulkText).length !== 1 ? 's' : ''} detected
              </p>
            )}
            <div style={{ display:'flex', gap:8, marginTop:16 }}>
              <Btn variant="ghost" onClick={() => setShowBulk(false)} style={{ flex:1 }}>Cancel</Btn>
              <Btn variant="primary" onClick={handleBulkAdd} disabled={!bulkText.trim()} style={{ flex:1 }}>
                <i className="fa-solid fa-plus"></i> Add Contacts
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
